// Motion tokens for the notifications popover.
//
// Values come from the transitions.dev motion scale (/transitions-polish), picked by
// *usage* (dropdown open/close, text swap, stagger …), not by nearest number. The rules
// that decide them (compositor-only, the project entrance curve, reduced-motion guards)
// and the tokens shared with the rest of the app live in styles/motion; they are
// re-exported here so the popover's modules keep one import.
export {
  prefersReducedMotion,
  EASE_ENTRANCE,
  EASE_SMOOTH_OUT,
  EASE_IN_OUT,
  HOVER_IN_MS,
  HOVER_OUT_MS,
  FRAME_SLACK_MS,
  hoverTint,
} from "../../styles/motion";

/** --ease-bounce: overshoot for the badge pop. Entrances only, never a close. */
export const EASE_BOUNCE = "cubic-bezier(0.34, 1.36, 0.64, 1)";

// ── Popover open / close (menu-dropdown recipe: anchored, grows from the bell) ──
/** --duration-fast: dropdown open. */
export const PANEL_OPEN_MS = 250;
/** --duration-quick: dropdown close. Closes are shorter so dismissal gets out of the way. */
export const PANEL_CLOSE_MS = 150;
/** --scale-medium: the pre-open scale the panel grows from. */
export const PANEL_OPEN_SCALE = 0.97;
/** --scale-tiny: closes barely shrink, so the exit reads as a fade, not a zoom. */
export const PANEL_CLOSE_SCALE = 0.99;

// ── Rows ──────────────────────────────────────────────────────────────────────
/** --duration-slow (content reveal) for a row that arrives while the panel is open. */
export const ROW_ENTER_MS = 400;
/** --distance-base: rows drop in from just above their slot. */
export const ROW_ENTER_DISTANCE = 8;
// × dismiss / Clear all mirror the swipe commit in useSwipeToDismiss (220ms slide on
// the entrance curve + 200ms linear fade). No token usage matches "row exit", so these
// are left at the gesture's values rather than forced onto the scale.
export const ROW_EXIT_SLIDE_MS = 220;
export const ROW_EXIT_FADE_MS = 200;
/** --duration-stagger: per-row offset for the Clear all cascade. */
export const STAGGER_MS = 40;
/** Cap the cascade at 7 offsets (7 × 40 = 280ms) to stay under the scale's ~300ms total. */
export const STAGGER_MAX_STEPS = 7;
/**
 * Clear all: rows scrolled out of view don't animate at all (nothing would see it,
 * and each running animation costs a layer). They're held invisible in their slot
 * until the cascade's batch is dropped, so the visible rows never shift mid-exit.
 */
export const EXIT_OFFSCREEN = -1;
/** Most rows a new batch can animate in: the panel (≤600px) never shows more. */
export const ROW_ENTER_MAX = 8;
/** --duration-fast on --ease-smooth-out: siblings sliding into a freed slot (FLIP). */
export const REFLOW_MS = 250;

// ── Small swaps and reveals ───────────────────────────────────────────────────
/** --duration-fast (icon swap): timestamp ↔ dismiss ×. Symmetric by rule, in = out. */
export const ICON_SWAP_MS = 250;
/** Icon-swap start scale for the × (transitions-dev icon swap default). */
export const ICON_SWAP_SCALE = 0.25;
/** --duration-quick (text swap): Load more ↔ Loading…. Symmetric by rule. */
export const TEXT_SWAP_MS = 150;
/** --distance-micro: text swap travel. */
export const TEXT_SWAP_DISTANCE = 4;
/** --duration-very-slow (text reveal) for the empty state after Clear all. */
export const REVEAL_MS = 500;
/** --distance-medium: text reveal travel. */
export const REVEAL_DISTANCE = 12;
/** --duration-very-slow (badge appear) on --ease-bounce. */
export const BADGE_POP_MS = 500;

// ── Upload progress bar ───────────────────────────────────────────────────────
/** --duration-very-slow (content reveal): the fill growing to the draft's share, and re-growing on a live update. */
export const PROGRESS_FILL_MS = 500;
/** Held back by a --duration-quick so the fill grows once the popover has mostly settled, not under its open. */
export const PROGRESS_FILL_DELAY_MS = 150;
